// Reusable Tailwind class strings for common UI patterns
// Colors: primary=#6A2473, danger=#ff9900, success=#ADD8E6, info=#FDF8F5

// --- Buttons ---
export const btnBase = 'tw:inline-flex tw:items-center tw:justify-center tw:rounded tw:font-medium tw:transition-colors tw:focus:outline-none tw:focus:ring-2 tw:focus:ring-offset-2 tw:cursor-pointer tw:border';
export const btnPrimary = `${btnBase} tw:bg-laud-purple tw:text-white tw:border-laud-purple tw:hover:bg-[#551d5c] tw:hover:border-[#4a1950] tw:focus:ring-laud-purple tw:disabled:opacity-65 tw:disabled:cursor-not-allowed`;
export const btnDanger = `${btnBase} tw:bg-[#a86000] tw:text-white tw:border-[#a86000] tw:hover:bg-[#8a5000] tw:hover:border-[#7a4600] tw:focus:ring-laud-orange tw:disabled:opacity-65 tw:disabled:cursor-not-allowed`;
export const btnSuccess = `${btnBase} tw:bg-[#ADD8E6] tw:text-black tw:border-[#ADD8E6] tw:hover:bg-[#8abfce] tw:hover:border-[#7db5c5] tw:focus:ring-[#ADD8E6] tw:disabled:opacity-65 tw:disabled:cursor-not-allowed`;
export const btnInfo = `${btnBase} tw:bg-[#FDF8F5] tw:text-black tw:border-[#FDF8F5] tw:hover:bg-[#cac6c4] tw:hover:border-[#bdb9b7] tw:focus:ring-[#FDF8F5] tw:disabled:opacity-65 tw:disabled:cursor-not-allowed`;
export const btnOutlinePrimary = `${btnBase} tw:bg-transparent tw:text-laud-purple tw:border-laud-purple tw:hover:bg-laud-purple tw:hover:text-white tw:focus:ring-laud-purple tw:disabled:opacity-65 tw:disabled:cursor-not-allowed`;
export const btnOutlineDanger = `${btnBase} tw:bg-transparent tw:text-[#a86000] tw:border-[#a86000] tw:hover:bg-[#a86000] tw:hover:text-white tw:focus:ring-laud-orange tw:disabled:opacity-65 tw:disabled:cursor-not-allowed`;
export const btnLink = 'tw:bg-transparent tw:border-none tw:p-0 tw:text-laud-purple tw:underline tw:hover:text-[#551d5c] tw:cursor-pointer';
export const btnSm = 'tw:px-2 tw:py-1 tw:text-sm';
export const btnMd = 'tw:px-3 tw:py-1.5 tw:text-base';
export const btnLg = 'tw:px-4 tw:py-2 tw:text-lg';

export function btnVariant(variant?: string) {
    switch (variant) {
        case 'primary': return btnPrimary;
        case 'danger': return btnDanger;
        case 'success': return btnSuccess;
        case 'info': return btnInfo;
        case 'outline-primary': return btnOutlinePrimary;
        case 'outline-danger': return btnOutlineDanger;
        case 'link': return btnLink;
        default: return btnPrimary;
    }
}

export function btnSize(size?: string) {
    switch (size) {
        case 'sm': return btnSm;
        case 'lg': return btnLg;
        default: return btnMd;
    }
}

// --- Alerts ---
export const alertBase = 'tw:relative tw:px-4 tw:py-3 tw:mb-4 tw:border tw:rounded';
export const alertDanger = `${alertBase} tw:text-[#994d00] tw:bg-[#fff5e6] tw:border-[#ffe0b3]`;
export const alertPrimary = `${alertBase} tw:text-[#3d1542] tw:bg-[#f0e4f2] tw:border-[#d4b3d9]`;
export const alertSuccess = `${alertBase} tw:text-[#5a8a96] tw:bg-[#eef7f9] tw:border-[#d4ecf1]`;
export const alertInfo = `${alertBase} tw:text-[#968f89] tw:bg-[#fefcfb] tw:border-[#fef5f0]`;

export function alertVariant(variant?: string) {
    switch (variant) {
        case 'danger': return alertDanger;
        case 'primary': return alertPrimary;
        case 'success': return alertSuccess;
        case 'info': return alertInfo;
        default: return alertDanger;
    }
}

// --- Cards ---
export const card = 'tw:bg-white tw:border tw:border-[#dee2e6] tw:rounded tw:shadow-sm';
export const cardHeader = 'tw:text-white tw:bg-[slategray] tw:px-4 tw:pt-4 tw:pb-0 tw:rounded-t';
export const cardBody = 'tw:p-4';
export const cardTitle = 'tw:mb-2 tw:text-xl tw:font-medium';
export const cardText = 'tw:mb-4';
export const cardGroup = 'tw:flex tw:flex-wrap';

// --- Forms ---
export const formGroup = 'tw:mb-2';
export const formLabel = 'tw:block tw:mb-1 tw:font-medium';
export const formControl = 'tw:block tw:w-full tw:px-3 tw:py-1.5 tw:text-base tw:border tw:border-[#dee2e6] tw:rounded tw:transition-colors tw:focus:border-laud-purple tw:focus:outline-none tw:focus:ring-2 tw:focus:ring-laud-purple/25';
export const formSelect = `${formControl} tw:appearance-auto`;
export const formCheck = 'tw:flex tw:items-center tw:gap-2';
export const formText = 'tw:block tw:mt-1 tw:text-sm tw:text-[#6c757d]';

// --- Tables ---
export const tableResponsive = 'tw:overflow-x-auto';
export const table = 'tw:w-full tw:border-collapse tw:text-left';
export const tableBordered = 'tw:[&_th]:border tw:[&_th]:border-[#dee2e6] tw:[&_td]:border tw:[&_td]:border-[#dee2e6]';
export const tableStriped = 'tw:[&_tbody_tr:nth-child(odd)]:bg-[#f8f9fa]';
export const tableHover = 'tw:[&_tbody_tr:hover]:bg-[#e9ecef]';
export const tableTh = 'tw:px-3 tw:py-2 tw:bg-[#f8f9fa] tw:border-b-2 tw:border-[#dee2e6] tw:font-medium tw:cursor-pointer';
export const tableTd = 'tw:px-3 tw:py-2';

// --- Modal ---
export const modalHeader = 'tw:flex tw:items-center tw:justify-between tw:p-4 tw:text-[#FDF8F5] tw:bg-purple-800 tw:rounded-t';
export const modalTitle = 'tw:text-lg tw:font-medium';
export const modalBody = 'tw:p-4';
export const modalFooter = 'tw:flex tw:items-center tw:justify-end tw:gap-2 tw:p-4 tw:border-t tw:border-[#dee2e6]';
export const modalCloseButton = 'tw:text-[#FDF8F5] tw:bg-transparent tw:border-none tw:text-2xl tw:cursor-pointer tw:leading-none tw:hover:opacity-75';

// --- Image (fluid) ---
export const imgFluid = 'tw:max-w-full tw:h-auto';
